import { useRef, useState } from 'react';
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { pageImages } from '../../data/siteData';
import PagePhotoViewer from '../../elements/PagePhotoViewer';

const images = pageImages['precrush-compactors-waste-compactors'];


function PreCrushCompactorsWasteCompactors() {
  const compactorCarouselRef = useRef(null);
  const dragState = useRef({ active: false, startX: 0, scrollLeft: 0 });
  const [activeCompactorSlide, setActiveCompactorSlide] = useState(0);

  const updateCompactorSlide = () => {
    const track = compactorCarouselRef.current;
    if (!track) return;

    const cards = Array.from(track.querySelectorAll('.cart-option-card'));
    const maxScroll = track.scrollWidth - track.clientWidth;
    const slideTargets = cards.map((card) => Math.min(card.offsetLeft - track.offsetLeft, maxScroll));

    if (track.scrollLeft >= maxScroll - 4) {
      setActiveCompactorSlide(cards.length - 1);
      return;
    }

    let currentIndex = 0;
    for (let index = 1; index < slideTargets.length; index += 1) {
      const threshold = (slideTargets[index - 1] + slideTargets[index]) / 2;
      if (track.scrollLeft >= threshold) currentIndex = index;
    }

    setActiveCompactorSlide(currentIndex);
  };

  const moveCompactorCarousel = (direction) => {
    const track = compactorCarouselRef.current;
    if (!track) return;

    const cards = Array.from(track.querySelectorAll('.cart-option-card'));
    const nextIndex = Math.min(Math.max(activeCompactorSlide + direction, 0), cards.length - 1);
    const nextCard = cards[nextIndex];
    const maxScroll = track.scrollWidth - track.clientWidth;

    track.scrollTo({
      left: Math.min(nextCard.offsetLeft - track.offsetLeft, maxScroll),
      behavior: 'smooth',
    });
    setActiveCompactorSlide(nextIndex);
  };

  const startCompactorDrag = (event) => {
    const track = compactorCarouselRef.current;
    if (!track) return;
    track.setPointerCapture?.(event.pointerId);
    dragState.current = {
      active: true,
      startX: event.clientX,
      scrollLeft: track.scrollLeft,
    };
    track.classList.add('dragging');
  };

  const moveCompactorDrag = (event) => {
    const track = compactorCarouselRef.current;
    if (!track || !dragState.current.active) return;
    event.preventDefault();
    track.scrollLeft = dragState.current.scrollLeft - (event.clientX - dragState.current.startX);
  };

  const stopCompactorDrag = (event) => {
    if (event?.currentTarget?.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    dragState.current.active = false;
    compactorCarouselRef.current?.classList.remove('dragging');
    window.setTimeout(updateCompactorSlide, 120);
  };

  return (
    <main className="detail-main">
      <section className="detail-layout">
        <aside>
          <span className="eyebrow">In This Section</span>
          <Link to="/closed-end-manual-tie-balers">Closed-End Manual Tie Balers</Link>
          <Link to="/open-end-automatic-tie-balers">Open-End Automatic Tie Balers</Link>
          <Link to="/wide-mouth-open-end-automatic-tie-balers">Wide-Mouth Open-End Automatic Tie Balers</Link>
          <Link to="/multi-materials-full-eject-closed-end-balers">Multi Materials Full-Eject Closed-End Balers</Link>
          <Link to="/open-end-manual-tie-balers">Open-End Manual Tie Balers</Link>
          <Link to="/cart-lifters-dumpers-dump-carts">Cart Lifters, Dumpers &amp; Dump Carts</Link>
          <Link to="/conveyors-bale-conveying-bagging-systems">Conveyors, Bale Conveying &amp; Bagging Systems</Link>
          <Link to="/used-clothing-balers">Used Clothing Balers</Link>
          <Link to="/cotton-gin-textile-baling-presses">Cotton Gin &amp; Textile Baling Presses</Link>
          <Link to="/biomass-cotton-gin-trash-baling-press">Biomass &amp; Cotton Gin Trash Baling Press</Link>
          <Link to="/vertical-down-packing-baling-presses-balers">Vertical &amp; Down-Packing Baling Presses &amp; Balers</Link>
          <Link to="/custom-designed-specialty-balers">Custom Designed &amp; Specialty Balers</Link>
          <Link className="active" to="/precrush-compactors-waste-compactors">PreCrush Compactors &amp; Waste Compactors</Link>
        </aside>
        <article className="article">
          <span className="eyebrow">Products</span>
          <h2>PreCrush Compactors &amp; Waste Compactors</h2>
          <div className="body-copy page-summary-copy">Compactors should be used only when dealing with <strong>general waste</strong> that has no meaningful recyclable content and to reduce the <strong>volume of waste</strong> so fewer expensive garbage pick-ups are necessary.</div>
          <div className="page-summary-notes">
              <span>Pre-crushes bulky loads before compaction</span>
              <span>Reduces landfill hauling trips</span>
              <span>Custom waste handling configurations</span>
          </div>
          <PagePhotoViewer title="PreCrush Compactors &amp; Waste Compactors" images={images} leadText={"Compactors should be used only when dealing with general waste that has no meaningful recyclable content and to reduce the volume of waste so fewer expensive garbage pick-ups are necessary."} eyebrow="Equipment Photos" />
          <div className="content-stack">
            <section className="content-block">
              <div className="body-copy">When waste needs to be destroyed and/or greatly reduced in volume, consider the C+M designed, highest quality <strong>PreCrushCompactor</strong> with many optional features for those demanding applications. Most “<strong>compactors</strong>” reduce waste from 1:2 and 1:4 ratios and a C+M PreCrushCompactor will likely double those for 1:4 to 1:8 ratios.</div>
            </section>
            <section className="content-block">
              <div className="body-copy">C+M's <strong>PreCrushCompactor</strong> has many unique features not found on other PreCrushers to solve problems that plague other PreCrushers. Some examples are the Crusher Door track and platen/ram plates are ALL replaceable wear materials designed for lubrication and wear NOT STEEL-ON-STEEL! The C+M Crusher Door has a guide-track cleaning device for clearing materials such as Styrofoam and wood chips. The C+M Crusher Door is shown with solid steel bars to break wood and other rigid materials.The C+M PreCrushCompactor is manufactured with C+M <strong>baling press</strong> QUALITY and not the lower “industry accepted” compactor quality.</div>
            </section>
           
           
            <section className="cart-carousel-section">
              <div className="cart-carousel-head">
                <div>
                  <span className="eyebrow">Compactor Options</span>
                  <div className="body-copy">C+M manufactures the CM <strong>PreCrushCompactor</strong>, but if your application is less demanding, C+M distributes quality low cost general <strong>waste compactors</strong>.</div>
                </div>
              </div>
              <div
                className="cart-carousel"
                ref={compactorCarouselRef}
                aria-label="General waste compactor options"
                onPointerDown={startCompactorDrag}
                onPointerMove={moveCompactorDrag}
                onPointerUp={stopCompactorDrag}
                onPointerCancel={stopCompactorDrag}
                onScroll={updateCompactorSlide}
                style={{ '--carousel-end-space': 'calc(100% - min(410px, 82vw))' }}
              >
                <figure className="cart-option-card conveyor-option-card">
                  <div className="cart-option-image conveyor-option-image">
                    <img loading="lazy" decoding="async" src="/img/equipment/compactors/stationary-compactor.jpg" alt="Stationary compactor 2.71 to 4.22 cubic yards" draggable="false" />
                  </div>
                  <figcaption>2.71 to 4.22 cubic yard Stationary Compactors</figcaption>
                </figure>
                <figure className="cart-option-card conveyor-option-card">
                  <div className="cart-option-image conveyor-option-image">
                    <img src="/img/thumbnails/PreCrushCompactorPic4.jpg" alt="Stationary compactor 1.58 to 2.31 cubic yards" draggable="false" />
                  </div>
                  <figcaption>1.58 to 2.31 cubic yard Stationary Compactors</figcaption>
                </figure>
                <figure className="cart-option-card conveyor-option-card">
                  <div className="cart-option-image conveyor-option-image">
                    <img loading="lazy" decoding="async" src="/img/equipment/compactors/self-contained-compactor.jpg" alt="Self-contained compactor 1.51 cubic yard" draggable="false" />
                  </div>
                  <figcaption>Compactors 1.51 cubic yard Self-Contained Compactors</figcaption>
                </figure>
              </div>
              <div className="cart-carousel-controls" aria-label="Compactor carousel controls">
                <button type="button" onClick={() => moveCompactorCarousel(-1)} aria-label="Show previous compactors">
                  <ChevronLeft size={22} />
                </button>
                <div className="carousel-dots" aria-hidden="true">
                  {[0, 1, 2].map((index) => (
                    <span key={index} className={index === activeCompactorSlide ? 'active' : ''}></span>
                  ))}
                </div>
                <button type="button" onClick={() => moveCompactorCarousel(1)} aria-label="Show next compactors">
                  <ChevronRight size={22} />
                </button>
              </div>
            </section>
            
              
              <div className="brochure-links">
                <a className="button" href="/pdf/e-brochure-CM-PreCrushCompactor-CM6090PCC40-8.pdf" target="_blank" rel="noreferrer">PDF Brochure <ArrowRight size={16} /></a>
              </div>
           
          </div>
          <Link className="button" to="/contact/request-quote">Request a Quote <ArrowRight size={16} /></Link>
        </article>
      </section>
    </main>
  );
}

export default PreCrushCompactorsWasteCompactors;
